import { apiRequest, apiRequestPaginated } from './client';
import type { Complaint } from '../types';

type ApiDisputeJob = {
  _id: string;
  title?: string;
  budget?: number;
  estimatedStartDate?: string;
  disputeStatus?: string;
  createdAt?: string;
  user?: { name?: string | null; email?: string; isSubscribed?: boolean } | null;
  provider?: { name?: string | null; email?: string; isSubscribed?: boolean } | null;
};

type DisputeSide = {
  initiatedBy?: string;
  reason?: string;
  pictures?: string[];
  description?: string;
};

type DisputeDetails = {
  jobDetails: ApiDisputeJob | null;
  transactionID: string | null;
  disputeUser?: DisputeSide | Record<string, never>;
  disputeProvider?: DisputeSide | Record<string, never>;
};

export const mapDisputeJob = (
  job: ApiDisputeJob,
  extras?: { description?: string; transactionId?: string }
): Complaint => ({
  id: job._id,
  jobId: job._id,
  submittedBy: job.user?.name || '—',
  submittedByType: 'Customer',
  against: job.provider?.name || '—',
  againstType: 'Provider',
  type: 'Dispute',
  description: extras?.description || job.title || '',
  createdAt: job.createdAt || '',
  status: 'Under Review',
  notes: [],
  history: [],
  transactionId: extras?.transactionId,
});

export const disputesApi = {
  getOngoingDisputes: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<{ disputes: Complaint[]; total: number }> => {
    const query = new URLSearchParams({
      page: String(params?.page || 1),
      limit: String(params?.limit || 1000),
    });

    const { data, pagination } = await apiRequestPaginated<{
      disputedJobs: ApiDisputeJob[];
    }>(`/admin/disputes/get-ongoing-disputes?${query.toString()}`, {
      method: 'GET',
      auth: true,
    });

    return {
      disputes: (data.disputedJobs || []).map((job) => mapDisputeJob(job)),
      total: pagination.totalItems || 0,
    };
  },

  getDisputeDetails: async (jobID: string): Promise<Complaint> => {
    const data = await apiRequest<{ disputeDetails: DisputeDetails }>(
      `/admin/disputes/get-dispute-details/${jobID}`,
      { method: 'GET', auth: true }
    );

    const details = data.disputeDetails;
    if (!details?.jobDetails) throw new Error('Dispute not found');

    const userDesc =
      details.disputeUser && 'description' in details.disputeUser
        ? details.disputeUser.description || details.disputeUser.reason || ''
        : '';
    const providerDesc =
      details.disputeProvider && 'description' in details.disputeProvider
        ? details.disputeProvider.description || details.disputeProvider.reason || ''
        : '';

    const description = [userDesc && `User: ${userDesc}`, providerDesc && `Provider: ${providerDesc}`]
      .filter(Boolean)
      .join('\n') || details.jobDetails.title || '';

    return mapDisputeJob(details.jobDetails, {
      description,
      transactionId: details.transactionID || undefined,
    });
  },

  resolveDisputeUser: async (jobID: string, transactionID: string): Promise<void> => {
    await apiRequest(
      `/admin/disputes/resolve-dispute-user?jobID=${jobID}&transactionID=${transactionID}`,
      { method: 'POST', auth: true }
    );
  },

  resolveDisputeProvider: async (jobID: string, transactionID: string): Promise<void> => {
    await apiRequest(
      `/admin/disputes/resolve-dispute-provider?jobID=${jobID}&transactionID=${transactionID}`,
      { method: 'POST', auth: true }
    );
  },
};
